/* eslint-disable no-restricted-globals */
import { computed, effect, Injectable, signal } from '@angular/core';
import { v4 as uuid } from 'uuid';

import { getZoneUnPatchedApi } from '@cosmos/zone-less';

import {
  injectStatsCollectorStorage,
  STATS_BATCHES_STORAGE_KEY,
} from './feature-stats-collector-storage.service';
import type { StatBatch, StatEvent } from './feature-stats-collector.types';

const id = 'stats-recording-indicator';

@Injectable({
  providedIn: 'root',
})
export class FeatureStatsCollectorService {
  readonly statBatches = signal<StatBatch[]>([]);

  readonly hasData = computed(() => this.statBatches().length > 0);

  private readonly _enabled = signal(false);

  constructor() {
    this._tryToSyncWithStorage();

    effect(() => {
      if (this._enabled()) {
        this._createRecordingIndicator();
      } else {
        this._clearRecordingIndicator();
      }
    });
  }

  async fetch(requestUrl: string, init: RequestInit): Promise<Response> {
    const nativeFetch = getZoneUnPatchedApi('fetch');

    if (!this.isEnabled()) {
      return nativeFetch(requestUrl, init);
    }

    // Parse request body
    const requestPayload = this._parseRequestBody(init?.body);

    const correlationId = uuid();
    // Create batch entry with request data
    const statBatch: StatBatch = {
      requestUrl,
      size: requestPayload.length,
      request: requestPayload,
      timestamp: new Date().toISOString(),
      correlationId,
    };

    this.appendStatBatch(statBatch);

    // Make the actual request and capture response
    try {
      const response = await nativeFetch(requestUrl, init);
      await this._updateBatchWithResponse(correlationId, response);
      return response;
    } catch (error) {
      this._updateBatchWithError(correlationId, error);
      // Throw back to the ASI stats sender so it can retry.
      throw error;
    }
  }

  isEnabled(): boolean {
    return this._enabled();
  }

  switchRecording(enabled: boolean): void {
    this._enabled.set(enabled);
  }

  appendStatBatch(data: StatBatch): void {
    this.statBatches.update((batches) => [...batches, data]);
  }

  clearStatBatches(): void {
    this.statBatches.set([]);
  }

  async downloadStatsAsJSON(batches = this.statBatches()): Promise<void> {
    const { downloadStatsAsJSON } = await import(
      './utils/feature-stats-collector.utils'
    );
    downloadStatsAsJSON(batches);
  }

  async downloadStatsAsCsv(batches = this.statBatches()): Promise<void> {
    const { downloadStatsAsCsv } = await import(
      './utils/feature-stats-collector.utils'
    );
    downloadStatsAsCsv(batches);
  }

  async downloadStatsAsXlsx(batches = this.statBatches()): Promise<void> {
    const { downloadStatsAsXlsx } = await import(
      './utils/feature-stats-collector.utils'
    );
    downloadStatsAsXlsx(batches);
  }

  private async _updateBatchWithResponse(
    correlationId: string,
    response: Response
  ): Promise<void> {
    try {
      const responseData = await response.clone().json();
      this.statBatches.update((batches) => {
        const batchIndexToUpdate = batches.findIndex(
          (batch) => batch.correlationId === correlationId
        );
        if (batchIndexToUpdate > -1) {
          const updatedBatches = [...batches];
          updatedBatches[batchIndexToUpdate] = {
            ...updatedBatches[batchIndexToUpdate],
            statusCode: response.status,
            response: responseData,
          };
          return updatedBatches;
        }
        return batches;
      });
    } catch {
      this._updateBatchWithError(
        correlationId,
        new Error('Failed to parse response JSON'),
        response.status
      );
    }
  }

  private _updateBatchWithError(
    correlationId: string,
    error: unknown,
    statusCode = 0
  ): void {
    this.statBatches.update((batches) => {
      const batchIndexToUpdate = batches.findIndex(
        (batch) => batch.correlationId === correlationId
      );
      if (batchIndexToUpdate > -1) {
        const updatedBatches = [...batches];
        updatedBatches[batchIndexToUpdate] = {
          ...updatedBatches[batchIndexToUpdate],
          statusCode,
          error: error instanceof Error ? error.message : 'Request failed',
        };
      }
      return batches;
    });
  }

  private _createRecordingIndicator(): void {
    if (document.getElementById(id)) {
      return;
    }

    const indicator = document.createElement('div');
    indicator.id = id;
    indicator.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      border: 4px solid red;
      z-index: 999999;
      animation: pulse 2s infinite;
    `;

    const style = document.createElement('style');
    style.id = `${id}-style`;
    style.textContent = `
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.3; }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(indicator);
  }

  private _clearRecordingIndicator(): void {
    document.getElementById(id)?.remove();
    document.getElementById(`${id}-style`)?.remove();
  }

  private _parseRequestBody(body: BodyInit | null | undefined): StatEvent[] {
    if (!body || typeof body !== 'string') return [];

    try {
      return JSON.parse(body);
    } catch {
      return [];
    }
  }

  private _tryToSyncWithStorage() {
    const storage = injectStatsCollectorStorage();
    if (!storage) return;

    // Load existing batches from storage
    const storedBatches = storage.getItem<StatBatch[]>(
      STATS_BATCHES_STORAGE_KEY
    );
    if (storedBatches) {
      this.statBatches.set(storedBatches);
    }

    // Sync to storage on changes
    effect(() => {
      const batches = this.statBatches();
      try {
        storage.setItem(STATS_BATCHES_STORAGE_KEY, batches);
      } catch {
        // // handle quota exceeded, e.g. drop oldest batch, clear storage, or log?
      }
    });
  }
}
